import { BEARER_PREFIX } from '@warehouselogix/shared';

/**
 * Thin fetch wrapper for the /admin and /platform tiers.
 *
 * ENVELOPE (frozen in shared/src/admin-contracts.ts): these tiers follow the
 * WorkLogix pattern — plain JSON bodies on success, standard Nest HTTP
 * exceptions ({ statusCode, message }) on error. This is NOT the floor tier's
 * ApiResult envelope, which is why web-common's createApiClient (which parses
 * the ErrorEnvelope shape) is not used here; only its auth surface is.
 */

/** Typed error for any non-2xx admin/platform response. */
export class AdminApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
  }
}

/** Async supplier of a bearer access token (useAuth().acquireToken). */
export type TokenProvider = () => Promise<string>;

export interface AdminHttpOptions {
  /** API origin, from `VITE_API_BASE`. Trailing slash tolerated. */
  baseUrl: string;
  /** Supplies the Entra bearer token. */
  getToken: TokenProvider;
  /** Invoked once on a 401 so the caller can start the sign-in redirect. */
  onUnauthorized: () => void;
  /**
   * Invoked on a 403 (not a member / insufficient role) BEFORE the error is
   * thrown, so the tenant context can re-check memberships. The 403 is still
   * thrown as an {@link AdminApiError} for the screen's ErrorBar.
   */
  onForbidden?: (message: string) => void;
}

export interface AdminRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON body; Content-Type is set when present. */
  body?: unknown;
  signal?: AbortSignal;
}

/** `request<T>(path, opts)` — path is a contract route (no leading slash needed). */
export type AdminHttp = <T>(path: string, options?: AdminRequestOptions) => Promise<T>;

function joinUrl(baseUrl: string, path: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

/**
 * Extract a human-readable message from a Nest error body. `message` may be a
 * string or (from ValidationPipe) an array of strings.
 */
async function nestErrorMessage(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as { message?: string | string[] };
    if (Array.isArray(data.message) && data.message.length > 0) {
      return data.message.join(' ');
    }
    if (typeof data.message === 'string' && data.message !== '') {
      return data.message;
    }
  } catch {
    // Non-JSON or empty body — fall through to the generic line.
  }
  return `Request failed with status ${response.status}.`;
}

/** Create the admin/platform HTTP function bound to a token provider. */
export function createAdminHttp(options: AdminHttpOptions): AdminHttp {
  return async function request<T>(path: string, opts: AdminRequestOptions = {}): Promise<T> {
    const { method = 'GET', body, signal } = opts;

    const headers = new Headers();
    if (body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('Authorization', `${BEARER_PREFIX}${await options.getToken()}`);

    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }
    if (signal !== undefined) {
      init.signal = signal;
    }

    const response = await fetch(joinUrl(options.baseUrl, path), init);

    if (response.status === 401) {
      options.onUnauthorized();
      throw new AdminApiError(401, 'Your session has expired. Redirecting to sign-in…');
    }
    if (response.status === 403) {
      // Membership guard said no (tenancy v1): not a member, or role too low.
      // Surface it like any other failure, but let the tenant context react.
      const message = await nestErrorMessage(response);
      options.onForbidden?.(message);
      throw new AdminApiError(403, message);
    }
    if (!response.ok) {
      throw new AdminApiError(response.status, await nestErrorMessage(response));
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  };
}

/**
 * Reduce an unknown thrown value to a user-safe display string for a
 * MessageBar. Never surfaces a raw stack or `[object …]`.
 */
export function toErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message !== '') {
    return err.message;
  }
  return 'Something went wrong. Please try again.';
}
